<template>
  <v-row class="match-height">
    <v-col cols="12">
      <v-card>
        <v-card-text class="d-flex mt-5">
          <v-avatar size="54" rounded color="primary" class="v-avatar-light-bg primary--text mt-1">
            <div>
              <p class="font-weight-medium mb-0">
                {{ getShortMonthName(eventModel.time) }}
              </p>
              <p class="text-xl primary--text font-weight-bold mb-0">
                {{ getDayOfMonth(eventModel.time) }}
              </p>
            </div>
          </v-avatar>

          <div class="ms-3">
            <v-card-title class="text--primary pt-1 pl-0">
              {{ eventModel.title }}
            </v-card-title>

            <span>
              <span v-html="markdownToHtml(eventModel.description)"></span>
            </span>
          </div>
        </v-card-text>
        <v-card-text>
          <v-divider></v-divider>
        </v-card-text>
        <v-card-text>
          <div class="d-flex align-center mt-2">
            <v-icon size="20" class="text-xl primary--text">
              {{ icons.mdiCalendar }}
            </v-icon>

            <div class="ms-2">
              <p class="text--primary mb-0">
                {{ getDayOfWeek(eventModel.time) }}
                {{ getShortDate(eventModel.time) }}
                {{ $t("Klo") }}
                {{ getTime(eventModel.time) }}
                <span v-if="eventModel.end_time">
                  -
                  <span v-if="!isSameDay(eventModel.time, eventModel.end_time)">
                    {{ getShortDate(eventModel.end_time) }} {{ $t("Klo") }}
                  </span>
                  {{ getTime(eventModel.end_time) }}
                </span>
              </p>
              <span class="text-xs" v-if="eventModel.enrollment_ends">
                {{ $t("Ilmoittautuminen päättyy") }}&nbsp;
                {{ getDateTime(eventModel.enrollment_ends) }}
              </span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col cols="12"></v-col>
    <v-col cols="12">
      <v-card>
        <v-card-title>{{ $t("Ilmoittautumiset") }}</v-card-title>
        <v-btn @click="excelExport" color="#428610" small class="ml-5 mb-3">
          <v-icon style="color: white">
            {{ icons.mdiMicrosoftExcel }}
          </v-icon>
          <span class="pl-2 white--text">{{ $t('Vie ilmoittautumiset') }}</span>
        </v-btn>
        <v-card-text>
          <v-data-table @pagination="onPagination" :headers="tableColumns" :items="enrollments" :loading="loading"
            mobile-breakpoint="0" :no-data-text="$t('Ei ilmoittautumisia')" :footer-props="{
              'items-per-page-options': [pageSize],
            }">
            <template #[`item.number`]="{ index }">
              {{ ((page - 1) * pageSize) + (index + 1) }}
            </template>

            <template #[`item.user`]="{ item }">
              {{ item.user }}
            </template>

            <template #[`item.insert_date`]="{ item }">
              {{ getDateTime(item.insert_date) }}
            </template>

            <template #[`item.comment`]="{ item }">
              <p style="white-space: pre-line;">
                {{ item.comment }}
              </p>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { onMounted, reactive, ref } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { useTranslations } from "@/@core/libs/i18n";
import useMarkdown from "@core/utils/useMarkdown";
import {
  mdiStarOutline,
  mdiCheckCircleOutline,
  mdiAccountOutline,
  mdiDotsHorizontal,
  mdiClockOutline,
  mdiMicrosoftExcel,
  mdiCalendar,
} from "@mdi/js";
import { writeFileXLSX, utils } from "xlsx";

import {
  getShortDate,
  getShortMonthName,
  getDayOfMonth,
  getDayOfWeek,
  getDateTime,
  isSameDay,
  getTime,
} from "@/@core/utils/datetime_helpers";

export default {
  props: ["id"],
  setup(props) {
    const loading = ref(false);
    const { t } = useTranslations();
    const { markdownToHtml } = useMarkdown();
    const tableColumns = [
      { text: t("#").toUpperCase(), value: "number", sortable: false },
      {
        text: t("Henkilö").toUpperCase(),
        value: "user",
        sortable: false,
      },
      {
        text: t("Ilmoitautumisaika").toUpperCase(),
        value: "insert_date",
        sortable: false,
      },
      {
        text: t("Kommentti").toUpperCase(),
        value: "comment",
        sortable: false,
      },
    ];

    const eventModel = reactive({
      title: "",
      description: "",
      time: "",
      end_time: null,
      enrollment_ends: null,
    });
    const enrollments = ref([]);
    const page = ref(1);
    const pageSize = 100;

    const fetchEvent = async (id) => {
      const response = await adminApi.getEvent(id);
      if (response.ok) {
        eventModel.title = response.data.title;
        eventModel.description = response.data.description;
        eventModel.time = response.data.time;
        eventModel.end_time = response.data.end_time;
        eventModel.enrollment_ends = response.data.enrollment_ends;
      } else {
        alert(response.error);
      }
    };

    const fetchEnrollments = async (id) => {
      loading.value = true;
      const response = await adminApi.getEnrollments(id);
      if (response.ok) {
        enrollments.value = response.data;
      } else {
        alert(response.error);
      }
      loading.value = false;
    };

    const excelExport = () => {
      var wb = utils.book_new();

      var data = [];
      data.push(['#',
        "Ilmoittautumisaika",
        "Henkilö",
        "Kommentti"]);

      let index = 1;
      enrollments.value.forEach(e => {
        data.push([
          index++,
          getDateTime(e.insert_date),
          e.user,
          e.comment
        ]);
      });

      var ws = utils.aoa_to_sheet(data);

      utils.book_append_sheet(wb, ws, "Sheet1");
      writeFileXLSX(wb, `${eventModel.title}_ilmoittautumiset.xlsx`);
    };

    const onPagination = (pagination) => {
      page.value = pagination.page;
    }

    onMounted(() => {
      if (props.id) {
        fetchEvent(props.id);
        fetchEnrollments(props.id);
      }
    });

    return {
      onPagination,
      page,
      pageSize,
      loading,
      eventModel,
      enrollments,
      getDayOfMonth,
      getShortMonthName,
      getDayOfWeek,
      getShortDate,
      getDateTime,
      getTime,
      excelExport,
      isSameDay,
      markdownToHtml,
      tableColumns,
      icons: {
        mdiStarOutline,
        mdiMicrosoftExcel,
        mdiCheckCircleOutline,
        mdiAccountOutline,
        mdiDotsHorizontal,
        mdiClockOutline,
        mdiCalendar,
      },
    };
  },
};
</script>

<style></style>